
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '@/contexts/AuthContext';

interface ErrorContext {
  component?: string;
  action?: string;
  additionalData?: Record<string, any>;
}

export const useSentryError = () => {
  const { user } = useAuth();

  // Set user context when user changes
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name || user.email?.split('@')[0],
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  const captureError = (error: Error, context?: ErrorContext) => {
    Sentry.withScope((scope) => {
      if (context?.component) {
        scope.setTag('component', context.component);
      }
      if (context?.action) {
        scope.setTag('action', context.action);
      }
      if (context?.additionalData) {
        scope.setContext('additionalData', context.additionalData);
      }
      scope.setLevel('error');
      Sentry.captureException(error);
    });
  };

  const captureMessage = (message: string, level: 'info' | 'warning' | 'error' = 'info', context?: ErrorContext) => {
    Sentry.withScope((scope) => {
      if (context?.component) {
        scope.setTag('component', context.component);
      }
      if (context?.action) {
        scope.setTag('action', context.action);
      }
      if (context?.additionalData) {
        scope.setContext('additionalData', context.additionalData);
      }
      scope.setLevel(level);
      Sentry.captureMessage(message);
    });
  };

  return { captureError, captureMessage };
};
